import React, {useRef} from 'react';
import type {BoxProps} from '@tempo/core';
import {Alert, AlertIcon, Box, Flex, Text} from '@tempo/core';
import {useSidebarContext} from '@components/Sidebar/SidebarProvider';
import {WarningIcon} from '@tempo/icons';
import storage from '@deezer/next/shared/utils/storage';
import {storageImpersonateKey} from '@constants/storageKeys';

type PageLayoutProps = {
	header?: React.ReactNode;
	children?: React.ReactNode;
	footer?: React.ReactNode;
	aside?: React.ReactNode;
} & BoxProps;

const PageLayout = ({
	header,
	footer,
	children,
	aside,
	...rest
}: PageLayoutProps) => {
	const headerRef = useRef<HTMLDivElement>(null);
	const isImpersonateSession = storage.get<boolean>(storageImpersonateKey);
	const {setHeaderScroll} = useSidebarContext();

	const handleScroll = (e: React.UIEvent<HTMLElement>) => {
		setHeaderScroll(e.currentTarget);
	};

	return (
		<Flex direction="column" position="relative" height="100%">
			{isImpersonateSession && (
				<Alert
					data-testid="impersonate-info-banner"
					status="warning"
					p="spacing.xs"
					ml="spacing.s"
					mt="spacing.s"
					w={{base: '97%', md: '98%'}}
				>
					<AlertIcon>
						<WarningIcon boxSize="icon.m" />
					</AlertIcon>
					<Text variant="caption.strong">
						You are logged in under an impersonate account. Be wary of your
						actions. Any change will be visible by the user.
					</Text>
				</Alert>
			)}
			<Box as="header" ref={headerRef} bg="background.neutral.primary.default">
				{header}
			</Box>
			<Flex
				direction="row"
				height="100%"
				width="100%"
				overflow="auto"
				flex="auto"
				position="relative"
			>
				<Box
					as="main"
					pb={{base: 'spacing.m', md: 'spacing.xl'}}
					height={'100%'}
					flex="auto"
					overflow="auto"
					onScroll={handleScroll}
					width="100%"
					{...rest}
				>
					{children}
				</Box>
				{aside && (
					<Box as="aside" height="100%" zIndex="docked">
						{aside}
					</Box>
				)}
			</Flex>
			{footer && <Box as="footer">{footer}</Box>}
		</Flex>
	);
};

export default PageLayout;
